/**
 * Hook for calculating missed captures with custom date range
 * Used by Report page for detailed missed captures analysis
 * Excludes periods when captures were intentionally disabled by user
 */

import { useState, useEffect, useMemo } from "react";
import type {
  Photo,
  TimerSettings,
  TimerSettingsHistoryEntry,
  MissedCapture,
  MobileConfig,
  TimelineEvent,
  CapturingStateEntry,
} from "@/types";
import { calculateMissedCaptures } from "@/utils/missedCapturesCalculator";
import { useStatusHistory } from "./useStatusHistory";
import { useCapturingStateHistory } from "./useCapturingStateHistory";
import { subscribeToTimerSettingsHistory } from "@/services/viewService";
import { subscribeToMobileConfig } from "@/services/configService";

interface UseMissedCapturesForReportParams {
  userId: string;
  projectId: string;
  viewId: string;
  photos: Photo[];
  timerSettings: TimerSettings | undefined;
  rangeStart: Date;
  rangeEnd: Date;
  captureOrigin?: Date;
  enabled?: boolean;
}

interface UseMissedCapturesForReportResult {
  missedCaptures: MissedCapture[];
  timelineEvents: TimelineEvent[];
  loading: boolean;
  error: Error | null;
  toleranceSeconds: number;
  stats: {
    missedCount: number;
    capturedCount: number;
    expectedCount: number;
    successRate: number;
  };
}

/**
 * Format time as HH:MM
 */
function formatTime(date: Date): string {
  return date.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
}

/**
 * Format interval in human readable form
 */
function formatInterval(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
}

/**
 * Convert capturing state history and settings history into timeline events
 */
function buildTimelineEvents(
  capturingHistory: CapturingStateEntry[],
  settingsHistory: TimerSettingsHistoryEntry[],
  rangeStart: Date,
  rangeEnd: Date
): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  // Convert capturing state changes to events
  for (const entry of capturingHistory) {
    const timestamp = entry.changedAt?.toDate?.();
    if (!timestamp) continue;

    // Skip entries outside range
    if (timestamp.getTime() < rangeStart.getTime() || timestamp.getTime() > rangeEnd.getTime()) {
      continue;
    }

    if (entry.capturing) {
      events.push({
        type: "resumed",
        timestamp,
        icon: "play_circle",
        label: `Capture enabled at ${formatTime(timestamp)}`,
      });
    } else {
      events.push({
        type: "paused",
        timestamp,
        icon: "pause_circle",
        label: `Capture paused at ${formatTime(timestamp)}`,
      });
    }
  }

  // Convert settings history to events (only interval changes)
  for (let i = 0; i < settingsHistory.length; i++) {
    const entry = settingsHistory[i];
    const timestamp = entry.changedAt?.toDate?.();
    if (!timestamp) continue;

    // Skip entries outside range
    if (timestamp.getTime() < rangeStart.getTime() || timestamp.getTime() > rangeEnd.getTime()) {
      continue;
    }

    // Only show interval changes if we have previous interval info
    if (entry.previousInterval && entry.previousInterval !== entry.interval) {
      events.push({
        type: "interval_changed",
        timestamp,
        icon: "timer",
        label: `Interval: ${formatInterval(entry.previousInterval)} → ${formatInterval(entry.interval)} at ${formatTime(timestamp)}`,
      });
    }
  }

  // Sort by timestamp descending (most recent first, like the timeline)
  events.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  return events;
}

/**
 * Hook for calculating missed captures with custom date range
 * Enriches results with nearest status snapshots
 */
export function useMissedCapturesForReport({
  userId,
  projectId,
  viewId,
  photos,
  timerSettings,
  rangeStart,
  rangeEnd,
  captureOrigin,
  enabled = true,
}: UseMissedCapturesForReportParams): UseMissedCapturesForReportResult {
  const [settingsHistory, setSettingsHistory] = useState<TimerSettingsHistoryEntry[]>([]);
  const [mobileConfig, setMobileConfig] = useState<MobileConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [_error] = useState<Error | null>(null);

  // Load status history
  const {
    findNearestSnapshot,
    loading: statusLoading,
    error: statusError,
  } = useStatusHistory({
    userId,
    projectId,
    viewId,
    enabled,
  });

  // Load capturing state history to get disabled periods and events
  const {
    capturingHistory,
    disabledPeriods,
    loading: capturingLoading,
    error: capturingError,
  } = useCapturingStateHistory({
    userId,
    projectId,
    viewId,
    rangeStart,
    rangeEnd,
    enabled,
  });

  // Subscribe to timer settings history
  useEffect(() => {
    if (!enabled || !userId || !projectId || !viewId) {
      return;
    }

    const unsubscribe = subscribeToTimerSettingsHistory(
      userId,
      projectId,
      viewId,
      (history) => {
        setSettingsHistory(history);
      },
      (err) => {
        console.error("Error loading timer settings history:", err);
      },
      100 // More history for longer date ranges
    );

    return () => unsubscribe();
  }, [userId, projectId, viewId, enabled]);

  // Subscribe to mobile config for tolerance setting
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const unsubscribe = subscribeToMobileConfig((config) => {
      setMobileConfig(config);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [enabled]);

  // Get tolerance from config
  const toleranceSeconds = useMemo(() => {
    return mobileConfig?.missedCaptureToleranceSeconds ?? 60;
  }, [mobileConfig]);

  // Calculate missed captures
  const missedCaptures = useMemo(() => {
    if (!enabled || !timerSettings || loading || statusLoading || capturingLoading) {
      return [];
    }

    // Don't calculate if interval is very short
    if (timerSettings.interval < 30) {
      return [];
    }

    // Calculate raw missed captures, excluding disabled periods
    const rawMissedCaptures = calculateMissedCaptures({
      photos,
      timerSettings,
      settingsHistory,
      toleranceSeconds,
      rangeStart,
      rangeEnd,
      captureOrigin,
      disabledPeriods,
    });

    // Enrich with nearest status snapshot
    return rawMissedCaptures.map((missed) => ({
      ...missed,
      nearestStatusSnapshot: findNearestSnapshot(missed.expectedAt),
    }));
  }, [
    enabled,
    photos,
    timerSettings,
    settingsHistory,
    toleranceSeconds,
    rangeStart,
    rangeEnd,
    captureOrigin,
    disabledPeriods,
    loading,
    statusLoading,
    capturingLoading,
    findNearestSnapshot,
  ]);

  // Calculate statistics
  const stats = useMemo(() => {
    const missedCount = missedCaptures.filter(m => m.status === "missed").length;
    const capturedCount = photos.length;
    const expectedCount = missedCount + capturedCount;
    const successRate = expectedCount > 0
      ? Math.round((capturedCount / expectedCount) * 1000) / 10
      : 100;

    return {
      missedCount,
      capturedCount,
      expectedCount,
      successRate,
    };
  }, [missedCaptures, photos]);

  // Build timeline events from capturing history and settings history
  const timelineEvents = useMemo(() => {
    if (!enabled || loading || capturingLoading) {
      return [];
    }
    return buildTimelineEvents(capturingHistory, settingsHistory, rangeStart, rangeEnd);
  }, [enabled, loading, capturingLoading, capturingHistory, settingsHistory, rangeStart, rangeEnd]);

  // Combine errors
  const combinedError = _error || statusError || capturingError;

  return {
    missedCaptures,
    timelineEvents,
    loading: loading || statusLoading || capturingLoading,
    error: combinedError,
    toleranceSeconds,
    stats,
  };
}
